import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Divider,
  CircularProgress,
  IconButton,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import sessionHandling from "services/utils/notificationUtils";
import { designWeights } from "services/constants/designTokens";
import { EmptyState } from "components/CustomComponents/EmptyState";
import { checkViewerPermission } from "services/utils/HelperFunction";
import { dashboard } from "./dashboardStyles";

// Recent Payslip — full-width row (image 2) showing the three most recent runs
// side by side. Uses the same `user-payslip-details` endpoint as My Zone >
// Payslip; the download icon routes to that screen where the actual PDF
// download/print flow already lives (kept there rather than duplicated here).
export const RecentPayslipSection = () => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, token, userDetails, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  const history = useHistory();
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);

  // This card previewed payslip data and linked to the Payslip screen for
  // every user, even one with no viewer access to "Payslip" at all -- Home
  // is meant to surface what's relevant to the signed-in user, not every
  // card regardless of access.
  const hasAccess = checkViewerPermission(
    userScreenAccess?.module_details,
    "Payslip",
    "Payslip",
  );

  useEffect(() => {
    let cancelled = false;
    const fetchPayslips = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${domain}payroll/operations/payrollprocessing/payrollruncycle/user-payslip-details/?user_id=${userDetails?.id}&company_id=${requestCompanyId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          if (!cancelled) setPayslips(Array.isArray(res) ? res : []);
        } else if (response.status === 409) {
          sessionHandling();
        }
      } catch (error) {
        console.error("[RecentPayslipSection] operation failed:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (userDetails?.id && hasAccess) fetchPayslips();
    else setLoading(false);
    return () => {
      cancelled = true;
    };
  }, [domain, token, userDetails?.id, hasAccess, requestCompanyId]);

  if (!hasAccess) return null;

  const recent = payslips.slice(0, 3);
  const goToPayslip = () => history.push("/admin/myzone/payslip");

  return (
    <Box sx={dashboard.cardSx}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Typography fontWeight={designWeights.heading} sx={{ color: dashboard.colors.text }}>
          Recent Payslip
        </Typography>
        <Typography sx={{ ...dashboard.linkSx, color: dashboard.colors.blue }} onClick={goToPayslip}>
          View All →
        </Typography>
      </Stack>

      {loading ? (
        <CircularProgress size={22} />
      ) : recent.length === 0 ? (
        <EmptyState subtitle="No payslips available yet." size={96} sx={{ py: 2 }} />
      ) : (
        <>
          <Grid container spacing={2}>
            {recent.map((item, idx) => (
              <Grid item xs={12} md={4} key={item.pay_schedule_id ?? idx}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRight: {
                      md: idx !== recent.length - 1 ? `1px solid ${dashboard.colors.subText}22` : "none",
                    },
                  }}
                >
                  <Box>
                    <Typography fontWeight={designWeights.medium} sx={{ color: dashboard.colors.text }}>
                      {item.pay_schedule_name || item.pay_schedule || "Payslip"}
                    </Typography>
                    {item.pay_date && (
                      <Typography variant="caption" sx={{ color: dashboard.colors.subText }}>
                        Paid on {item.pay_date}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {item.net_pay != null && item.net_pay !== "" && (
                      <Typography fontWeight={designWeights.heading} sx={{ color: dashboard.colors.green }}>
                        ₹ {item.net_pay}
                      </Typography>
                    )}
                    <IconButton size="small" onClick={goToPayslip} sx={{ border: `1px solid ${dashboard.colors.subText}22` }}>
                      <FileDownloadOutlinedIcon sx={{ fontSize: 18, color: dashboard.colors.subText }} />
                    </IconButton>
                  </Stack>
                </Stack>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ mt: 1.5 }} />
          <Typography
            sx={{ ...dashboard.linkSx, color: dashboard.colors.blue, textAlign: "center", mt: 1.5 }}
            onClick={goToPayslip}
          >
            Go to Payslips →
          </Typography>
        </>
      )}
    </Box>
  );
};
